<template>
  <div class="app-shell admin-layout">
    <aside class="sidebar">
      <div class="sidebar-brand">
        <img class="brand-logo" :src="logoUrl" alt="白医云" />
        <div>
          <div class="brand-title">白医云·医中心端</div>
        </div>
      </div>

      <nav class="sidebar-nav">
        <div class="menu-group">
          <button
            class="menu-parent"
            type="button"
            :class="{ active: isDashboardRoute }"
            @click="goDashboard"
          >
            <span class="menu-parent__label">
              <span class="menu-icon">{{ dashboardMenu.icon }}</span>
              <span>{{ dashboardMenu.title }}</span>
            </span>
          </button>
        </div>

        <div
          v-for="group in visibleMenuGroups"
          :key="group.id"
          class="menu-group"
        >
          <button
            class="menu-parent"
            type="button"
            :class="{ active: isGroupRouteActive(group.id) }"
            @click="toggleGroup(group.id)"
          >
            <span class="menu-parent__label">
              <span class="menu-icon">{{ group.icon }}</span>
              <span>{{ group.title }}</span>
            </span>
            <span class="menu-arrow" :class="{ open: isGroupOpen(group.id) }">›</span>
          </button>
          <div v-show="isGroupOpen(group.id)" class="menu-children">
            <router-link
              v-for="item in visibleMenuItems(group)"
              :key="item.to"
              :to="item.to"
              exact
              active-class="active"
            >{{ item.title }}</router-link>
          </div>
        </div>
      </nav>

      <div class="sidebar-footer">
        <button type="button" class="btn-ghost" @click="delegateLogout">退出登录</button>
      </div>
    </aside>

    <div class="main">
      <div class="main-header">
        <header class="topbar">
          <div>
<!--            <div class="topbar-kicker">管理中心</div>-->
            <div class="topbar-title">{{ pageTitle }}</div>
          </div>
          <div class="topbar-right">
            <div class="status-pill" v-if="appInstance">
              <span
                class="status-dot"
                :class="appInstance.onlineStatus === '1' ? 'is-online' : 'is-offline'"
              ></span>
              <el-button
                :type="appInstance.onlineStatus === '1' ? 'success' : 'danger'"
                size="mini"
                round
                plain
                @click="appInstance.doctorStatusChange()"
              >
                {{ appInstance.onlineStatus === '1' ? '上线' : '下线' }}
              </el-button>
              <el-button
                size="mini"
                round
                plain
                icon="el-icon-time"
                @click="delegateShowVisitTime"
              >
                设置就诊时间
              </el-button>
            </div>
            <span class="user-pill">{{ userLabel }}</span>
          </div>
        </header>

        <RouteTabs @refresh="refreshView" />
      </div>

      <div class="content" :class="contentClass">
        <keep-alive>
          <router-view :key="viewKey($route.path)" class="content-viewport" />
        </keep-alive>
      </div>
    </div>
  </div>
</template>

<script>
import RouteTabs from '@/components/RouteTabs/index.vue'
import { addTabFromRoute } from '@/utils/tabsStore'
import logoUrl from '@/assets/logo.png'
import {
  dashboardMenu,
  menuGroups,
  menuGroupIdForPath,
  hasLoginTypeFlag
} from '@/router/menus'

export default {
  name: 'AdminLayout',
  components: {
    RouteTabs
  },
  data() {
    return {
      logoUrl: logoUrl,
      dashboardMenu: dashboardMenu,
      loginType: '',
      openGroups: [],
      viewReloadSeed: {}
    }
  },
  computed: {
    pageTitle() {
      if (this.$route.name === 'dashboard' || this.$route.path === '/') {
        return '工作台'
      }
      return (this.$route.meta && this.$route.meta.title) || '工作台'
    },
    userLabel() {
      const name = localStorage.getItem('userName') || '医生'
      return name + ' · 中心端'
    },
    appInstance() {
      return this.$root.$children[0]
    },
    isDashboardRoute() {
      return this.$route.name === 'dashboard' || this.$route.path === '/'
    },
    isDashboard() {
      return this.isDashboardRoute
    },
    contentClass() {
      return {
        'content--dashboard': this.isDashboard,
        'content--page': !this.isDashboard,
        'content--biochemistry': this.$route.path.indexOf('/center/biochemistry') !== -1
      }
    },
    visibleMenuGroups() {
      const self = this
      return menuGroups.filter(function (group) {
        if (group.requireLoginType && !hasLoginTypeFlag(self.loginType)) {
          return false
        }
        return self.visibleMenuItems(group).length > 0
      })
    }
  },
  watch: {
    '$route.path': {
      handler: function (path) {
        const groupId = menuGroupIdForPath(path)
        this.openGroups = groupId && groupId !== dashboardMenu.id ? [groupId] : []
        addTabFromRoute(this.$route)
      },
      immediate: true
    }
  },
  created() {
    this.loginType = localStorage.getItem('loginType') || ''
  },
  methods: {
    visibleMenuItems(group) {
      const self = this
      return group.children.filter(function (item) {
        if (item.hidden) {
          return false
        }
        if (item.requireLoginType && !hasLoginTypeFlag(self.loginType)) {
          return false
        }
        return true
      })
    },
    isGroupRouteActive(groupId) {
      return menuGroupIdForPath(this.$route.path) === groupId
    },
    isGroupOpen(id) {
      return this.openGroups.indexOf(id) !== -1
    },
    goDashboard() {
      this.openGroups = []
      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
    },
    toggleGroup(id) {
      if (id === dashboardMenu.id) {
        this.goDashboard()
        return
      }
      if (this.isGroupOpen(id)) {
        this.openGroups = []
      } else {
        this.openGroups = [id]
      }
    },
    viewKey(path) {
      const seed = this.viewReloadSeed[path] || 0
      return path + '#' + seed
    },
    refreshView() {
      const path = this.$route.path
      this.$set(this.viewReloadSeed, path, (this.viewReloadSeed[path] || 0) + 1)
    },
    delegateShowVisitTime() {
      if (this.appInstance && this.appInstance.showVisitTimeDialog) {
        this.appInstance.showVisitTimeDialog()
      }
    },
    delegateLogout() {
      if (this.appInstance && this.appInstance.logout) {
        this.appInstance.logout()
      }
    }
  }
}
</script>

<style scoped>
.app-shell {
  height: 100vh;
  overflow: hidden;
  display: flex;
  background: #f5f7fb;
}

.sidebar {
  width: 260px;
  flex: 0 0 260px;
  height: 100vh;
  padding: 18px 14px;
  background: #ffffff;
  color: #334155;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-right: 1px solid #e5e7eb;
  overflow: hidden;
  box-sizing: border-box;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px 16px;
  border-bottom: 1px solid #edf2f7;
}

.brand-logo {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  object-fit: contain;
  display: block;
}

.brand-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.sidebar-nav {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.menu-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.menu-parent {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #334155;
  font: inherit;
  padding: 12px 14px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  box-sizing: border-box;
}

.menu-parent:hover {
  background: #f8fafc;
  border-color: #dbeafe;
  color: #2563eb;
}

.menu-parent.active {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #2563eb;
  font-weight: 700;
}

.menu-parent__label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.menu-icon {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
  flex: 0 0 24px;
}

.menu-parent.active .menu-icon {
  background: #dbeafe;
  color: #2563eb;
}

.menu-arrow {
  font-size: 18px;
  line-height: 1;
  color: #cbd5e1;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

.menu-arrow.open {
  transform: rotate(90deg);
}

.menu-children {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 10px;
  border-left: 1px solid #e5e7eb;
  margin-left: 12px;
}

.menu-children a {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 10px;
  color: #475569;
  text-decoration: none;
  background: #f8fafc;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  font-size: 13px;
}

.menu-children a:hover {
  background: #f1f5f9;
  color: #2563eb;
}

.menu-children a.active {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #2563eb;
  font-weight: 700;
}

.sidebar-footer {
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 6px;
}

.btn-ghost {
  width: 100%;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #334155;
  border-radius: 12px;
  padding: 11px 14px;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
}

.btn-ghost:hover {
  background: #f8fafc;
  border-color: #dbeafe;
  color: #2563eb;
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100vh;
  overflow: hidden;
}

.main-header {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #eef2f7;
}

.topbar {
  flex-shrink: 0;
  height: 52px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-bottom: none;
}

.main >>> .route-tabs-bar {
  flex-shrink: 0;
}

.topbar-kicker {
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 2px;
  line-height: 1.2;
}

.topbar-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-pill {
  padding: 9px 14px;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
  font-weight: 600;
  border: 1px solid #dbeafe;
  font-size: 13px;
}

.status-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  padding: 4px 12px;
  background: #f8fafc;
  flex-wrap: wrap;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.is-online {
  background: #00b42a;
  box-shadow: 0 0 0 3px rgba(0, 180, 42, 0.2);
}

.status-dot.is-offline {
  background: #f53f3f;
  box-shadow: 0 0 0 3px rgba(245, 63, 63, 0.2);
}

.content {
  flex: 1;
  min-height: 0;
  padding: 16px 20px 20px;
  overflow-y: auto;
  overflow-x: hidden;
}

.content.content--dashboard {
  overflow: hidden;
  padding: 0;
}

.content.content--page {
  display: block;
  overflow-y: auto;
  overflow-x: hidden;
}

.content.content--page >>> .content-viewport {
  display: block;
  height: auto;
  min-height: auto;
  overflow: visible;
}

@media (max-width: 1100px) {
  .sidebar {
    width: 240px;
    flex-basis: 240px;
  }
}
</style>

<style>
.admin-layout .content.content--dashboard .content-viewport.dashboard {
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/layouts/AdminLayout.vue